import { GuarantorDto } from "api/tenant";
import { useAppSelector } from "app/hooks";
import { ValidationTracker } from "components/domain/ValidationTracker/ValidationTracker";
import { selectReferentialsData } from "features/referentials/selectors/selectReferentialIdByCode";
import { Form, Formik, FormikProps } from "formik"
import { useDialog } from "hooks/useDialog";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tenantGuarantorFormConfig } from "../../form/tenantGuarantorFormConfig";
import { mapTenantGuarantorFormValues } from "../../mappers/mapTenantGuarantorFormValues";
import { TenantGuarantorFormValues } from "../../types/tenantGuarantorFormValues";
import { TenantGuarantorLayout } from "./TenantGuarantorLayout";

interface TenantGuarantorFormProps {
  onSubmit: (guarantor: GuarantorDto) => void
  guarantor?: GuarantorDto
}

export const TenantGuarantorForm: FC<TenantGuarantorFormProps> = ({ onSubmit }) => {
  const { setDialogActions, closeDialog } = useDialog()
  const [isFormValid, setIsFormValid] = useState(false)

  const formikRef = useRef<FormikProps<TenantGuarantorFormValues>>(null)

  const referentialsData = useAppSelector(selectReferentialsData)
  const initialValues = useMemo(() => {
    return tenantGuarantorFormConfig.initialValues;
  }, []);

  const validationSchema = useMemo(() => {
    return tenantGuarantorFormConfig.validationSchema;
  }, []);

  const handleSubmit = useCallback((values: TenantGuarantorFormValues) => {
    const guarantorDto = mapTenantGuarantorFormValues(values, referentialsData)
    onSubmit(guarantorDto)
    closeDialog()
  }, [onSubmit, referentialsData, closeDialog])

  useEffect(() => {
    setDialogActions({
      submitLabel: "Valider",
      closeLabel: "Fermer",
      submitDisabled: !isFormValid,
      onSubmit: () => {
        formikRef.current?.handleSubmit()
      }
    })
  }, [setDialogActions, isFormValid])

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid)
  }, [])

  return <Formik<TenantGuarantorFormValues>
    innerRef={formikRef}
    initialValues={initialValues}
    validationSchema={validationSchema}
    onSubmit={handleSubmit}
  >
    {
      ({ isValid }) => (
        <>
          <ValidationTracker isValid={isValid} onValidationChange={handleValidationChange} />
          <Form className="flex flex-col gap-6">
            <TenantGuarantorLayout />
          </Form>
        </>
      )
    }
  </Formik>
}
